import { FieldList, Radio, Stack, Text, tokens, Button } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { Widget, WidgetPreview } from '@hotjar/feedback/components/common/Wizard/WidgetPreview';
import type { WidgetConfig } from '@hotjar/feedback/components/common/Wizard/WidgetPreview/WidgetPreview';
import type { Settings as ForwardToSlackSettings } from '@hotjar/webapp/features/Forwarding/ForwardToSlack';
import type { Settings as ForwardToEmailSettings } from '@hotjar/webapp/features/Forwarding/ForwardToEmail';
import type { MsTeamsFeedbackSettings as ForwardToMsTeamsSettings } from '@hotjar/integrations';
import type { WebhooksSettings } from '@hotjar/integrations/domains/webhooks/api';
import { useTranslation } from 'react-i18next';
import { Trans } from '@hotjar/translations';
import type { TargetingRule } from '../../../../../domain/common/TargetingRule/TargetingRule';
import { RULE_TRIGGER_TYPE } from '../../../../../domain/common/TargetingRule/TargetingRule';
import { WidgetSummaryBullet } from './WidgetSummaryBullet';
import { TARGETING_MODE } from '../../../../common/Wizard/PagesTargeting/PagesTargeting';
import { validateTrafficCoverage } from '../../../../common/Wizard/TrafficCoverage/TrafficCoverage';
import { WidgetSummaryFooter } from './WidgetSummaryFooter';
import { WidgetSummaryError } from './WidgetSummaryError';

const StyledRadio = styled(Radio)`
  margin-bottom: 0;
`;

const Summary = styled(Stack)`
  padding: ${tokens.spacer.x3} ${tokens.spacer.x8};
  flex: 1;
  align-items: flex-start;
  justify-content: flex-start;
`;

const Description = styled(Stack)`
  max-width: 350px;
`;

const OptionLabel = styled.span<{ capitalize?: boolean }>`
  ${({ capitalize }) => capitalize === true && `text-transform: capitalize;`}
  font-weight: 600;
`;

const PreviewContainer = styled.div`
  padding: ${tokens.spacer.x3};
  position: relative;
  width: 428px;
  flex-shrink: 0;
  background: ${tokens.color.neutral_100};
  border-left: 1px solid ${tokens.color.ui_border_light};
`;

const StyledFieldList = styled(FieldList)`
  width: auto;
`;

const Footer = styled(WidgetSummaryFooter)`
  margin-top: ${tokens.spacer.x2};
`;

const TargetingButtonWrapper = styled.div`
  margin-top: ${tokens.spacer.x2};
`;

const BoldText = styled(Text)`
  font-weight: bold;
`;

export interface WidgetSummaryProps {
  previewConfig: WidgetConfig;
  targeting: TargetingRule[] | undefined;
  isActive: boolean;
  onStatusChange: (status: boolean) => void;
  pageTargetingMode: TARGETING_MODE;
  usersTargetingMode: TARGETING_MODE;
  trafficCoverage: number;
  jumpToSection: (sectionName: string) => void;
  forwardToSlackSettings: ForwardToSlackSettings | undefined;
  forwardToEmailSettings: ForwardToEmailSettings | undefined;
  forwardToMsTeamsSettings: ForwardToMsTeamsSettings | undefined;
  forwardToWebhooksSettings: WebhooksSettings | undefined;
  widgetName: string;
  widgetDescription: string;
  onSave: (shouldClose?: boolean) => boolean;
  saveInProgress: boolean;
  mode: 'edit' | 'create';
  isValid: boolean;
  hasNameErrors: boolean;
  hasAppearanceErrors: boolean;
  hasStepsErrors: boolean;
  hasTargetingErrors: boolean;
}

export const WidgetSummary = ({
  targeting,
  isActive,
  onStatusChange,
  previewConfig,
  pageTargetingMode,
  usersTargetingMode,
  trafficCoverage,
  jumpToSection,
  forwardToSlackSettings,
  forwardToMsTeamsSettings,
  forwardToWebhooksSettings,
  forwardToEmailSettings,
  widgetName,
  widgetDescription,
  onSave,
  saveInProgress,
  mode,
  isValid,
  hasNameErrors,
  hasAppearanceErrors,
  hasStepsErrors,
  hasTargetingErrors,
}: WidgetSummaryProps): JSX.Element => {
  const { t } = useTranslation('feedback');
  const deviceRules = useMemo(
    () => (targeting ?? []).filter((rule) => rule.component === RULE_TRIGGER_TYPE.DEVICE),
    [targeting],
  );
  const forwardingActive = useMemo(() => {
    if (forwardToSlackSettings?.enabled === true) {
      return true;
    }
    if (forwardToEmailSettings?.enabled === true) {
      return true;
    }

    if (forwardToMsTeamsSettings?.enabled === true) {
      return true;
    }

    if (forwardToWebhooksSettings?.enabled === true) {
      return true;
    }

    return false;
  }, [
    forwardToSlackSettings,
    forwardToEmailSettings,
    forwardToMsTeamsSettings,
    forwardToWebhooksSettings,
  ]);

  return (
    <Stack direction="row" customSpacing="0">
      <Summary spacing="x4" direction="column">
        <Description spacing="x1" direction="column">
          <Text textType="subtitle1">{widgetName || 'New Feedback widget'}</Text>
          <Text textType="body2">{widgetDescription}</Text>
        </Description>
        {(hasNameErrors || hasAppearanceErrors || hasStepsErrors || hasTargetingErrors) && (
          // grouped section errors for name, appearance and steps sections
          <Stack direction="column" spacing="x1">
            {hasNameErrors && (
              <WidgetSummaryError errorMessage={t('wizard.section.summary.name-error')} />
            )}
            {hasAppearanceErrors && (
              <WidgetSummaryError errorMessage={t('wizard.section.summary.appearance-error')} />
            )}
            {hasStepsErrors && (
              <WidgetSummaryError errorMessage={t('wizard.section.summary.steps-error')} />
            )}
            {hasTargetingErrors && (
              <WidgetSummaryError errorMessage={t('wizard.section.summary.targeting-error')} />
            )}
          </Stack>
        )}
        <StyledFieldList size="m" title={t('wizard.section.summary.targeting')}>
          <WidgetSummaryBullet
            icon="device_multiple"
            hasError={deviceRules.length === 0}
            errorMessage={t('wizard.section.summary.devices-error')}
            content={
              <>
                {t('wizard.section.summary.displays-on')}{' '}
                {deviceRules.length === 3 ? (
                  <OptionLabel>{t('wizard.section.summary.all-devices')}</OptionLabel>
                ) : (
                  <OptionLabel capitalize>
                    {deviceRules
                      .map((rule) => t(`wizard.device-selector.${rule.pattern}`))
                      .join(', ')}
                  </OptionLabel>
                )}
              </>
            }
          />
          <WidgetSummaryBullet
            icon="page_empty"
            hasError={!pageTargetingMode}
            errorMessage={t('wizard.section.summary.pages-error')}
            content={
              <>
                {t('wizard.section.summary.displays-on')}{' '}
                <OptionLabel>
                  {pageTargetingMode === TARGETING_MODE.ALL
                    ? t('wizard.section.targeting.page.options.all')
                    : t('wizard.section.targeting.page.options.specific.title')}
                </OptionLabel>
              </>
            }
          />
          <WidgetSummaryBullet
            icon="user_multiple"
            content={
              <>
                {t('wizard.section.summary.displays-to')}{' '}
                <OptionLabel>
                  {usersTargetingMode === TARGETING_MODE.ALL
                    ? t('wizard.section.summary.all-users')
                    : t('wizard.section.summary.selected-users')}
                </OptionLabel>
              </>
            }
          />
          <WidgetSummaryBullet
            icon="site"
            hasError={!validateTrafficCoverage(trafficCoverage)}
            errorMessage={t('wizard.section.summary.coverage-error')}
            content={
              <>
                {t('wizard.section.summary.displays-to')}{' '}
                <OptionLabel>
                  {t('wizard.section.summary.coverage', { trafficCoverage })}
                </OptionLabel>
              </>
            }
          />
          <TargetingButtonWrapper>
            <Button
              kind="secondary"
              size="s"
              icon="plus"
              onClick={() => jumpToSection('targeting')}
            >
              {t('wizard.section.summary.edit-targeting')}
            </Button>
          </TargetingButtonWrapper>
        </StyledFieldList>
        {forwardingActive &&
          forwardToEmailSettings !== undefined &&
          forwardToSlackSettings !== undefined &&
          forwardToMsTeamsSettings !== undefined &&
          forwardToWebhooksSettings !== undefined && (
            <StyledFieldList size="m" title={t('wizard.section.summary.responses')}>
              {forwardToEmailSettings.enabled && (
                <WidgetSummaryBullet
                  icon="email"
                  hasError={!forwardToEmailSettings.valid}
                  errorMessage={t('wizard.section.summary.error-email')}
                  content={
                    <Trans
                      i18nKey="feedback:wizard.section.summary.send-email"
                      count={forwardToEmailSettings.emails.length}
                    >
                      label
                      <OptionLabel>
                        {{ value: forwardToEmailSettings.emails.join(', ') }}
                      </OptionLabel>
                    </Trans>
                  }
                />
              )}
              {forwardToSlackSettings.enabled && (
                <WidgetSummaryBullet
                  icon="slack"
                  hasError={!forwardToSlackSettings.valid}
                  errorMessage={t('wizard.section.summary.error-slack')}
                  content={
                    <>
                      {forwardToSlackSettings.channels && (
                        <Trans
                          i18nKey="feedback:wizard.section.summary.send-to-slack"
                          count={forwardToSlackSettings.channels.length}
                        >
                          label
                          <OptionLabel>
                            {{ value: forwardToSlackSettings.channels.join(', ') }}
                          </OptionLabel>
                        </Trans>
                      )}
                    </>
                  }
                />
              )}
              {forwardToMsTeamsSettings.enabled && (
                <WidgetSummaryBullet
                  icon="microsoft_teams"
                  hasError={!forwardToMsTeamsSettings.valid}
                  errorMessage={t('wizard.section.summary.error-ms-teams')}
                  content={
                    <>
                      {forwardToMsTeamsSettings.webhooks && (
                        <Trans
                          i18nKey="feedback:wizard.section.summary.ms-teams"
                          count={forwardToMsTeamsSettings.webhooks.length}
                        >
                          label
                          <OptionLabel>
                            {{ value: forwardToMsTeamsSettings.webhooks.length }}
                          </OptionLabel>
                        </Trans>
                      )}
                    </>
                  }
                />
              )}
              {forwardToWebhooksSettings.enabled && (
                <WidgetSummaryBullet
                  icon="webhooks"
                  hasError={!forwardToWebhooksSettings.valid}
                  errorMessage={t('wizard.section.summary.error-webhooks')}
                  content={
                    forwardToWebhooksSettings.webhooks && (
                      <Trans
                        i18nKey="feedback:wizard.section.summary.webhooks"
                        count={forwardToWebhooksSettings.webhooks.length}
                      >
                        label <BoldText>{{ value: forwardToWebhooksSettings.webhooks }}</BoldText>
                      </Trans>
                    )
                  }
                />
              )}
            </StyledFieldList>
          )}
        <FieldList size="s" title={t('wizard.section.summary.status')}>
          <StyledRadio
            name="status"
            isChecked={isActive === true}
            onChange={() => onStatusChange(true)}
            labelContent={t('wizard.section.summary.active')}
          />
          <StyledRadio
            name="status"
            isChecked={isActive === false}
            onChange={() => onStatusChange(false)}
            labelContent={t('wizard.section.summary.inactive')}
          />
        </FieldList>
        <Footer
          mode={mode}
          onSave={() => onSave(false)}
          onSaveAndClose={() => onSave(true)}
          saveInProgress={saveInProgress}
          isValid={isValid}
        />
      </Summary>
      <PreviewContainer>
        <WidgetPreview
          widget={Widget.FEEDBACK}
          widgetConfig={previewConfig}
          showDeviceSelector
          iframeId="feedback-preview"
        />
      </PreviewContainer>
    </Stack>
  );
};
